/**
 * Formula Engine for the pricing system.
 *
 * Supported syntax:
 *  - Plain numbers:            150   |  3.14
 *  - Arithmetic:               2 + 2 * 10 - (5 / 2)
 *  - Price-level references:   [P1], [P2], … (1-based index)
 *  - Built-in functions:       MIN(a,b)  MAX(a,b)  ROUND(n,d)  FLOOR(n)  CEIL(n)
 *                              ABS(n)  TRUNC(n)  SQRT(n)  POW(a,b)  AVG(...args)
 *
 * @module formula-engine
 */

// ─── Token Pattern ─────────────────────────────────────────────────────────────
/** Matches price-level reference tokens like [P1], [P2], … (case-insensitive) */
export const PRICE_LEVEL_TOKEN_RE = /\[P(\d+)\]/gi;

/** Matches special external variable tokens like [Cost], [Avg Cost] (case-insensitive) */
export const SPECIAL_TOKEN_RE = /\[(?:Cost|Avg\s+Cost)\]/gi;

/** All characters that are legal inside a formula expression (after substitution) */
export const SAFE_EXPRESSION_RE = /^[\d\s+\-*/().,%]+$/;

/** Supported built-in function names (case-insensitive) */
export const BUILTIN_FUNCTIONS = ['MIN', 'MAX', 'ROUND', 'FLOOR', 'CEIL', 'ABS', 'TRUNC', 'SQRT', 'POW', 'AVG'];

// ─── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Returns true if the value is a formula string (starts with a [Pn] token or
 * contains an arithmetic operator that suggests it is not a plain number).
 *
 * @param {string} value
 * @returns {boolean}
 */
export function isFormula(value) {
    if (!value || typeof value !== 'string') return false;
    const trimmed = value.trim();
    if (trimmed === '') return false;

    // Use a fresh local regex for the test to avoid any lastIndex or shared state issues.
    // This regex matches [P1], [p2], etc.
    const tokenRegex = /\[P(\d+)\]/i;
    const specialTokenRegex = /\[(?:Cost|Avg\s+Cost)\]/i;
    const hasToken = tokenRegex.test(trimmed) || specialTokenRegex.test(trimmed);

    // It's a formula if it contains any reference token or common mathematical operators/functions
    return hasToken
        || /[+\-*/(]/.test(trimmed)
        || /MIN|MAX|ROUND|FLOOR|CEIL|ABS|TRUNC|SQRT|POW|AVG/i.test(trimmed);
}

// ─── Parse ─────────────────────────────────────────────────────────────────────

/**
 * Parses and validates a formula string.
 *
 * @param {string} formulaString - The raw formula entered by the user.
 * @param {number} priceLevelCount - Total number of available price levels.
 * @returns {{ valid: boolean, tokens: string[], referencedIndices: number[], error: string | null }}
 */
export function parseFormula(formulaString, priceLevelCount) {
    if (!formulaString || typeof formulaString !== 'string') {
        return {valid: false, tokens: [], referencedIndices: [], error: 'Formula is empty.'};
    }
    const trimmed = formulaString.trim();

    // Collect all [Pn] token occurrences
    const referencedIndices = [...getReferencedLevels(trimmed)];

    // Validate referenced indices are within range
    for (const idx of referencedIndices) {
        if (idx < 1 || idx > priceLevelCount) {
            return {
                valid: false,
                tokens: [],
                referencedIndices,
                error: `[P${idx}] references a price level that does not exist. Valid range: [P1]–[P${priceLevelCount}].`,
            };
        }
    }

    // Substitute tokens with placeholder numbers so we can validate the expression
    const substituted = trimmed
        .replace(PRICE_LEVEL_TOKEN_RE, '1')
        .replace(SPECIAL_TOKEN_RE, '1')
        .replace(new RegExp(BUILTIN_FUNCTIONS.join('|'), 'gi'), '');

    if (!SAFE_EXPRESSION_RE.test(substituted.replace(/,/g, ''))) {
        return {
            valid: false,
            tokens: [],
            referencedIndices,
            error: 'Formula contains unsupported characters or functions.',
        };
    }

    // Quick bracket-balance check
    let depth = 0;
    for (const ch of trimmed) {
        if (ch === '(') depth++;
        if (ch === ')') depth--;
        if (depth < 0) return {valid: false, tokens: [], referencedIndices, error: 'Mismatched parentheses.'};
    }
    if (depth !== 0) return {valid: false, tokens: [], referencedIndices, error: 'Mismatched parentheses.'};

    const tokens = trimmed.match(PRICE_LEVEL_TOKEN_RE) || [];
    return {valid: true, tokens, referencedIndices, error: null};
}

// ─── Evaluate ──────────────────────────────────────────────────────────────────

/**
 * Evaluates a formula string, substituting [Pn] references with the provided
 * resolved values. Supports built-in price-management functions.
 *
 * @param {string} formulaString - The formula to evaluate.
 * @param {Record<number, number|null>} resolvedValues - Map of 1-based price-level
 *        index → resolved numeric value (or null if unknown).
 * @param {Record<string, number|null>} specialResolvedValues - Map of special variable names (e.g. "Cost", "Avg Cost") to their resolved numeric values.
 * @returns {{ result: number | null, error: string | null }}
 */
export function evaluateFormula(formulaString, resolvedValues = {}, specialResolvedValues = {}) {
    if (!formulaString || typeof formulaString !== 'string') {
        return {result: null, error: 'No formula provided.'};
    }
    const trimmed = formulaString.trim();

    // Plain number shortcut
    const asNum = Number(trimmed);
    if (!isNaN(asNum) && trimmed !== '') return {result: asNum, error: null};

    // Replace [Pn] and special tokens with their values
    let expression = trimmed.replace(PRICE_LEVEL_TOKEN_RE, (_, indexStr) => {
        const idx = parseInt(indexStr, 10);
        const val = resolvedValues[idx];
        if (val === undefined || val === null) {
            return 'null';
        }
        return String(Number(val));
    }).replace(SPECIAL_TOKEN_RE, (match) => {
        const isAvg = /avg/i.test(match);
        const val = isAvg ? specialResolvedValues['Avg Cost'] : specialResolvedValues['Cost'];
        if (val === undefined || val === null) {
            return 0;
        }
        return String(Number(val));
    });

    // If any unresolved reference remains (returned "null"), we cannot evaluate
    if (expression.includes('null')) {
        return {result: null, error: 'One or more referenced price levels have no value yet.'};
    }

    // Inject safe built-in helpers and evaluate
    try {
        // eslint-disable-next-line no-new-func
        const evaluate = new Function(
            'MIN', 'MAX', 'ROUND', 'FLOOR', 'CEIL', 'ABS', 'TRUNC', 'SQRT', 'POW', 'AVG',
            `"use strict"; return (${expression});`
        );
        const result = evaluate(
            Math.min,
            Math.max,
            (n, d = 2) => Math.round(n * Math.pow(10, d)) / Math.pow(10, d),
            Math.floor,
            Math.ceil,
            Math.abs,
            Math.trunc,
            Math.sqrt,
            Math.pow,
            (...args) => args.reduce((a, b) => a + b, 0) / args.length,
        );
        if (typeof result !== 'number' || !isFinite(result)) {
            return {result: null, error: 'Formula produced a non-numeric result.'};
        }
        return {result: Math.round(result * 1e10) / 1e10, error: null};
    } catch (e) {
        return {result: null, error: `Invalid expression: ${e.message}`};
    }
}

// ─── Get Referenced Levels ─────────────────────────────────────────────────────

/**
 * Returns the set of 1-based price-level indices referenced by the formula.
 *
 * @param {string} formulaString
 * @returns {Set<number>}
 */
export function getReferencedLevels(formulaString) {
    if (!formulaString) return new Set();
    const indices = new Set();
    for (const match of formulaString.matchAll(PRICE_LEVEL_TOKEN_RE)) {
        indices.add(parseInt(match[1], 10));
    }
    return indices;
}

// ─── Circular Dependency Detection ─────────────────────────────────────────────

/**
 * Detects circular dependencies among a set of price-level formulas.
 *
 * @param {Record<number, string>} formulaMap - Map of 1-based price-level index → formula string.
 *        Price levels without a formula (or with plain-number formulas) can be omitted.
 * @returns {Array<number[]>} - An array of cycles, each cycle being an ordered list
 *          of 1-based price-level indices that form a dependency loop. Empty array = no cycles.
 */
export function detectCircularDependencies(formulaMap) {
    // Build adjacency: idx → Set of indices that idx depends on
    const deps = {};
    for (const [idx, formula] of Object.entries(formulaMap)) {
        deps[parseInt(idx, 10)] = getReferencedLevels(formula);
    }

    const allNodes = new Set(Object.keys(deps).map(Number));

    const WHITE = 0; // unvisited
    const GRAY = 1;  // in current DFS path
    const BLACK = 2; // fully processed

    const color = {};
    for (const node of allNodes) color[node] = WHITE;

    const cycles = [];

    const dfs = (node, path) => {
        color[node] = GRAY;
        path.push(node);

        for (const neighbour of deps[node]) {
            if (!allNodes.has(neighbour)) continue;
            if (color[neighbour] === GRAY) {
                // Found a cycle — extract the loop portion from path
                const cycleStart = path.indexOf(neighbour);
                cycles.push([...path.slice(cycleStart), neighbour]);
            } else if (color[neighbour] === WHITE) {
                dfs(neighbour, path);
            }
        }

        path.pop();
        color[node] = BLACK;
    };

    for (const node of allNodes) {
        if (color[node] === WHITE) dfs(node, []);
    }

    return cycles;
}

// ─── Resolve All Formulas (Topological) ────────────────────────────────────────

/**
 * Evaluates all price-level formulas in dependency order, resolving each value
 * before it is used by a dependent formula. Levels without formulas keep their
 * base value from `baseValues`.
 *
 * @param {Record<number, string>} formulaMap - 1-based index → formula string.
 * @param {Record<number, number|null>} baseValues - 1-based index → current numeric price.
 * @param {Record<string, number|null>} specialResolvedValues - Map of special variable names (e.g. "Cost", "Avg Cost") to their resolved numeric values.
 * @returns {{ resolvedValues: Record<number, number|null>, errors: Record<number, string> }}
 */
export function resolveAllFormulas(formulaMap, baseValues, specialResolvedValues) {
    const resolved = {...baseValues};
    const errors = {};

    // Simple iterative resolution (up to N passes to handle ordering)
    const indices = Object.keys(formulaMap).map(Number);
    const maxPasses = indices.length + 1;

    for (let pass = 0; pass < maxPasses; pass++) {
        let changed = false;
        for (const idx of indices) {
            const formula = formulaMap[idx];
            if (!formula || !isFormula(formula)) continue;
            const {result, error} = evaluateFormula(formula, resolved, specialResolvedValues);
            if (error) {
                errors[idx] = error;
            } else if (result !== resolved[idx]) {
                resolved[idx] = result;
                delete errors[idx];
                changed = true;
            }
        }
        if (!changed) break;
    }

    return {resolvedValues: resolved, errors};
}
